import { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Link as ReactLink, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useResetPasswordMutation } from "@/app/features/auth/authApiSlice";
import { delftBlue, flame, neutral } from "@/theme/colors";
import logoContainerSvg from "@/assets/Logo Container.svg";
import {
  ADMIN_PASSWORD_REGEX,
  ADMIN_PASSWORD_REGEX_MESSAGE,
} from "@/constants/password";

const ResetPasswordPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [resetPassword, { isLoading }] = useResetPasswordMutation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const formik = useFormik({
    initialValues: {
      password: "",
      confirmPassword: "",
      submit: null,
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .min(8, "Password must be at least 8 characters")
        .max(20, "Password must be at most 20 characters")
        .matches(ADMIN_PASSWORD_REGEX, ADMIN_PASSWORD_REGEX_MESSAGE)
        .required("Password is required"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password")], "Passwords don't match")
        .required("Confirm password is required"),
    }),
    onSubmit: async (values, helpers) => {
      try {
        if (!token) {
          helpers.setErrors({ submit: "Invalid reset token" });
          return;
        }

        const { data, error } = await resetPassword({
          token,
          password: values.password,
          confirmPassword: values.confirmPassword,
        });

        if (error) {
          helpers.setStatus({ success: false });
          helpers.setErrors({
            submit: error?.data?.message || "Failed to reset password",
          });
          helpers.setSubmitting(false);
          return;
        }

        // Show success message and redirect
        helpers.setStatus({
          success: true,
          message: data?.message || "Password reset successfully",
        });
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      } catch (err) {
        helpers.setStatus({ success: false });
        helpers.setErrors({ submit: err.message || "An error occurred" });
        helpers.setSubmitting(false);
      }
    },
  });

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword((prev) => !prev);
  };

  if (!token) {
    navigate("/login");
    return null;
  }

  return (
    <Box
      sx={{
        width: "100vw",
        height: "100vh",
        maxWidth: "100vw",
        maxHeight: "100vh",
        overflow: "hidden",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundImage: "url('/images/Background.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        padding: { xs: 1.5, sm: 2, md: 2.5 },
      }}
    >
      <Box
        sx={{
          width: { xs: "100%", sm: "95%", md: "auto" },
          maxWidth: { xs: "100%", sm: "1200px" },
          maxHeight: { xs: "100%", sm: "90vh", md: "85vh" },
          borderRadius: { xs: 2, sm: 3 },
          display: "inline-flex",
          justifyContent: "flex-start",
          alignItems: "stretch",
          gap: 0,
          overflow: "hidden",
          backgroundColor: "white",
          boxShadow: "0px 4px 20px rgba(0,0,0,0.1)",
        }}
      >
        {/* Left Side - Splash Screen */}
        <Box
          sx={{
            width: { xs: 0, md: 384 },
            minWidth: { md: 384 },
            display: { xs: "none", md: "inline-flex" },
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: delftBlue[500],
            alignSelf: "stretch",
            overflow: "hidden",
            padding: { md: 2 },
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: 2,
              width: "100%",
              maxHeight: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: 2,
                width: "100%",
              }}
            >
              {/* Logo Container */}
              <Box
                component="img"
                src={logoContainerSvg}
                alt="OP DOX Logo"
                sx={{
                  width: { md: "62.5%", lg: "75%" },
                  maxWidth: { md: 240, lg: 288 },
                  height: "auto",
                  aspectRatio: "1 / 1",
                  flexShrink: 0,
                }}
              />

              {/* Description */}
              <Typography
                sx={{
                  width: "100%",
                  textAlign: "center",
                  color: delftBlue[50],
                  fontSize: { md: 16, lg: 18 },
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 400,
                  lineHeight: 1.5,
                  paddingX: 2,
                }}
              >
                All safety documentation, activity pre-start forms, and incident
                reports.
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Right Side - Form Container */}
        <Box
          sx={{
            flex: 1,
            display: "inline-flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: { xs: 1.5, sm: 2 },
            padding: { xs: 2, sm: 3, md: 4 },
            backgroundColor: "white",
            overflowY: "auto",
            maxHeight: "100%",
            minWidth: { xs: "100%", md: 400 },
          }}
        >
          {/* Logo for Mobile */}
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: { xs: 1.5, sm: 2 },
              width: "100%",
              backgroundColor: delftBlue[500],
              borderRadius: { xs: 2, sm: 2.5 },
              padding: { xs: 2, sm: 2.5 },
              marginBottom: { xs: 1.5, sm: 2 },
            }}
          >
            <Box
              component="img"
              src={logoContainerSvg}
              alt="OP DOX Logo"
              sx={{
                width: { xs: 120, sm: 150 },
                height: { xs: 120, sm: 150 },
                flexShrink: 0,
              }}
            />
            {/* Description for Mobile */}
            <Typography
              sx={{
                width: "100%",
                textAlign: "center",
                color: delftBlue[50],
                fontSize: { xs: 13, sm: 14, md: 16 },
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 400,
                lineHeight: 1.5,
                paddingX: { xs: 2, sm: 3 },
              }}
            >
              All safety documentation, activity pre-start forms, and incident
              reports.
            </Typography>
          </Box>
          {/* Form Title */}
          <Box
            sx={{
              width: "100%",
              maxWidth: 384,
              display: "inline-flex",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: 0.75,
              marginBottom: { xs: 0.5, sm: 1 },
            }}
          >
            <Typography
              sx={{
                color: delftBlue[500],
                fontSize: { xs: "1.5rem", sm: "1.875rem" },
                fontFamily: "Audiowide, sans-serif",
                fontWeight: 400,
                lineHeight: 1.5,
              }}
            >
              Reset Password
            </Typography>
          </Box>

          {/* Form */}
          <Box
            component="form"
            noValidate
            onSubmit={formik.handleSubmit}
            sx={{
              paddingX: { xs: 0, sm: 0.75 },
              paddingY: { xs: 1, sm: 1.25 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              gap: { xs: 1.25, sm: 1.5 },
              width: "100%",
              maxWidth: 384,
            }}
          >
            {/* Password Input */}
            <Box
              sx={{
                alignSelf: "stretch",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                gap: 1,
                minHeight:
                  formik.touched.password && formik.errors.password
                    ? "auto"
                    : "auto",
                paddingBottom:
                  formik.touched.password && formik.errors.password ? 2.5 : 0,
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  minWidth: { xs: "100%", sm: 384 },
                  maxWidth: { xs: "100%", sm: 384 },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  alignItems: "flex-start",
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    alignSelf: "stretch",
                    paddingBottom: 0.5,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                  }}
                >
                  <Typography
                    sx={{
                      alignSelf: "stretch",
                      color: delftBlue[500],
                      fontSize: 16,
                      fontFamily: "Audiowide, sans-serif",
                      fontWeight: 400,
                      lineHeight: 1.5,
                    }}
                  >
                    Enter New Password
                  </Typography>
                </Box>
                <Box sx={{ width: "100%", position: "relative" }}>
                  <TextField
                    fullWidth
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      !!(formik.touched.password && formik.errors.password)
                    }
                    helperText={
                      formik.touched.password && formik.errors.password
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleTogglePasswordVisibility}
                            edge="end"
                            sx={{
                              color: neutral[400],
                              "&:hover": {
                                color: delftBlue[500],
                              },
                            }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    FormHelperTextProps={{
                      sx: {
                        position: "absolute",
                        top: "100%",
                        margin: 0,
                        marginTop: 0.5,
                        whiteSpace: "nowrap",
                      },
                    }}
                    sx={{
                      width: "100%",
                      "& .MuiOutlinedInput-root": {
                        height: 56,
                        paddingLeft: 0.75,
                        paddingRight: 0.75,
                        paddingTop: 1,
                        paddingBottom: 1,
                        backgroundColor: "white",
                        borderRadius: 2,
                        outline: `1px ${neutral[100]} solid`,
                        outlineOffset: "-1px",
                        "& fieldset": {
                          border: "none",
                        },
                        "& input": {
                          color: delftBlue[500],
                          fontSize: 16,
                          fontFamily: "Montserrat, sans-serif",
                          fontWeight: 400,
                          lineHeight: 1.5,
                          "&::placeholder": {
                            color: neutral[400],
                            opacity: 1,
                          },
                        },
                        "&.Mui-error": {
                          outline: `1px ${flame[500]} solid`,
                        },
                      },
                      "& .MuiFormHelperText-root": {
                        marginLeft: 0,
                        marginTop: 0.5,
                        fontSize: 12,
                        fontFamily: "Montserrat, sans-serif",
                        color: flame[500],
                        position: "absolute",
                        top: "100%",
                        whiteSpace: "nowrap",
                      },
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {/* Confirm Password Input */}
            <Box
              sx={{
                alignSelf: "stretch",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                gap: 1,
                minHeight:
                  formik.touched.confirmPassword &&
                  formik.errors.confirmPassword
                    ? "auto"
                    : "auto",
                paddingBottom:
                  formik.touched.confirmPassword &&
                  formik.errors.confirmPassword
                    ? 2.5
                    : 0,
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  minWidth: { xs: "100%", sm: 384 },
                  maxWidth: { xs: "100%", sm: 384 },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  alignItems: "flex-start",
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    alignSelf: "stretch",
                    paddingBottom: 0.5,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                  }}
                >
                  <Typography
                    sx={{
                      alignSelf: "stretch",
                      color: delftBlue[500],
                      fontSize: 16,
                      fontFamily: "Audiowide, sans-serif",
                      fontWeight: 400,
                      lineHeight: 1.5,
                    }}
                  >
                    Confirm Password
                  </Typography>
                </Box>
                <Box sx={{ width: "100%", position: "relative" }}>
                  <TextField
                    fullWidth
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formik.values.confirmPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      !!(
                        formik.touched.confirmPassword &&
                        formik.errors.confirmPassword
                      )
                    }
                    helperText={
                      formik.touched.confirmPassword &&
                      formik.errors.confirmPassword
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleToggleConfirmPasswordVisibility}
                            edge="end"
                            sx={{
                              color: neutral[400],
                              "&:hover": {
                                color: delftBlue[500],
                              },
                            }}
                          >
                            {showConfirmPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    FormHelperTextProps={{
                      sx: {
                        position: "absolute",
                        top: "100%",
                        margin: 0,
                        marginTop: 0.5,
                        whiteSpace: "nowrap",
                      },
                    }}
                    sx={{
                      width: "100%",
                      "& .MuiOutlinedInput-root": {
                        height: 56,
                        paddingLeft: 0.75,
                        paddingRight: 0.75,
                        paddingTop: 1,
                        paddingBottom: 1,
                        backgroundColor: "white",
                        borderRadius: 2,
                        outline: `1px ${neutral[100]} solid`,
                        outlineOffset: "-1px",
                        "& fieldset": {
                          border: "none",
                        },
                        "& input": {
                          color: delftBlue[500],
                          fontSize: 16,
                          fontFamily: "Montserrat, sans-serif",
                          fontWeight: 400,
                          lineHeight: 1.5,
                          "&::placeholder": {
                            color: neutral[400],
                            opacity: 1,
                          },
                        },
                        "&.Mui-error": {
                          outline: `1px ${flame[500]} solid`,
                        },
                      },
                      "& .MuiFormHelperText-root": {
                        marginLeft: 0,
                        marginTop: 0.5,
                        fontSize: 12,
                        fontFamily: "Montserrat, sans-serif",
                        color: flame[500],
                        position: "absolute",
                        top: "100%",
                        whiteSpace: "nowrap",
                      },
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {/* Success/Error Message */}
            {formik.status?.message && (
              <Typography
                sx={{
                  color: formik.status.success ? delftBlue[500] : flame[500],
                  fontSize: 14,
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 400,
                  alignSelf: "stretch",
                }}
              >
                {formik.status.message}
              </Typography>
            )}
            {formik.errors.submit && (
              <Typography
                sx={{
                  color: flame[500],
                  fontSize: 14,
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 400,
                  alignSelf: "stretch",
                }}
              >
                {formik.errors.submit}
              </Typography>
            )}

            {/* Reset Password Button */}
            <Button
              fullWidth
              type="submit"
              disabled={isLoading}
              sx={{
                alignSelf: "stretch",
                height: 56,
                paddingX: 1,
                paddingY: 0.75,
                backgroundColor: flame[500],
                borderRadius: 2,
                outline: `1px ${flame[500]} solid`,
                outlineOffset: "-1px",
                display: "inline-flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 0.75,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: flame[600],
                },
                "&:disabled": {
                  backgroundColor: neutral[300],
                  outline: `1px ${neutral[300]} solid`,
                },
              }}
            >
              <Typography
                sx={{
                  color: "white",
                  fontSize: 16,
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 600,
                }}
              >
                {isLoading ? "Resetting..." : "Reset Password"}
              </Typography>
            </Button>

            {/* Back to Login Link */}
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                paddingTop: { xs: 1, sm: 1.5 },
              }}
            >
              <Typography
                component={ReactLink}
                to="/login"
                sx={{
                  color: delftBlue[500],
                  fontSize: 14,
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 400,
                  textDecoration: "underline",
                  cursor: "pointer",
                  "&:hover": {
                    color: delftBlue[400],
                  },
                }}
              >
                Back to Login
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ResetPasswordPage;
